import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import { checkoutSubsciptions, fetchSubscriptionProducts } from '../../redux/PaymentSlice/paymentSlice';
import { performanceMonitor } from '../../utils/performanceMonitor';
import { X, Sparkles, Check, HelpCircle } from 'lucide-react';

// ─── static plan data ────────────────────────────────────────────────────────

const PLAN_META = {
    free:      { tag: null,          recommended: false, features: ['3–5 hiring signals / week', 'Basic job matching', 'Limited Plays'] },
    weekly:    { tag: 'Burst hiring', recommended: false, features: ['Full hiring signals', 'Smarter matching', 'Unlimited Plays'] },
    monthly:   { tag: 'Recommended', recommended: true,  features: ['Everything in Weekly', 'Priority AI quota', 'Unlimited recruiter lookups', 'Daily coaching'] },
    quarterly: { tag: 'Save 22%',    recommended: false, features: ['Everything in Monthly', '3 months locked in', 'Quarterly career report', 'Founding-member discount lock'] },
};

const COMPARE_ROWS = [
    { label: 'Hiring signal access',    free: 'Limited',   weekly: 'Full',      monthly: 'Full',      quarterly: 'Full' },
    { label: 'AI Assistant',            free: '5 / day',   weekly: 'Unlimited', monthly: 'Unlimited', quarterly: 'Unlimited' },
    { label: 'Recruiter lookups',       free: '3 / week',  weekly: '50',        monthly: '200',       quarterly: '600' },
    { label: 'Daily tasks coach',       free: '—',         weekly: '—',         monthly: '✓',         quarterly: '✓' },
    { label: 'Quarterly career report', free: '—',         weekly: '—',         monthly: '—',         quarterly: '✓' },
    { label: 'Priority support',        free: '—',         weekly: '✓',         monthly: '✓',         quarterly: '✓' },
];

const FAQ = [
    { q: 'Can I switch plans anytime?',      a: 'Yes — upgrade or downgrade in one click. We prorate the difference and credit your account.' },
    { q: 'What happens to unused credits?',  a: 'Credits roll over forever on Monthly and Quarterly. Weekly resets each cycle.' },
    { q: 'Is there a refund window?',        a: '7-day money-back guarantee on every paid plan, no questions asked.' },
];

// ─── helpers ─────────────────────────────────────────────────────────────────

const formatUsd  = (cents) => cents == null ? '0' : (Number(cents) / 100).toFixed(0);
const cycleFor   = (p) => p?.plan?.billingCycle || p?.metadata?.billing;
const orderIndex = (p) => ({ weekly: 0, monthly: 1, quarterly: 2, annual: 3 })[cycleFor(p)] ?? 99;

const intervalLabel = (p) => {
    const c = cycleFor(p);
    if (c === 'weekly')   return 'per week';
    if (c === 'quarterly') return 'per quarter';
    if (c === 'annual')    return 'per year';
    return 'per month';
};

const planTitle = (p) => {
    const c = cycleFor(p);
    if (c === 'weekly')    return 'Weekly';
    if (c === 'quarterly') return 'Quarterly';
    if (c === 'annual')    return 'Annual';
    return 'Monthly';
};

// ─── component ───────────────────────────────────────────────────────────────

const SubscriptionModal = ({ isOpen, onClose }) => {
    const dispatch = useDispatch();
    const user     = useSelector((s) => s.auth.user);
    const { checkoutLoading, subscriptionProducts, subLoading, subError } = useSelector((s) => s.payments);

    const [clickedPriceId, setClickedPriceId] = useState(null);
    const [checkoutError,  setCheckoutError]  = useState(null);

    const sortedProducts = useMemo(() => {
        const list = Array.isArray(subscriptionProducts) ? [...subscriptionProducts] : [];
        return list.sort((a, b) => {
            const d = orderIndex(a) - orderIndex(b);
            return d !== 0 ? d : (a.unit_amount || 0) - (b.unit_amount || 0);
        });
    }, [subscriptionProducts]);

    const isFreeUser       = !user?.subscription?.subscriptionId || user?.subscription?.tier === 'free';
    const currentTier      = user?.subscription?.tier || 'free';
    const currentCycleName = user?.billingCycle || user?.subscription?.billingCycle;

    const isCurrentPaidCard = (p) => {
        if (!user?.subscription?.subscriptionId || currentTier === 'free') return false;
        return currentCycleName === cycleFor(p);
    };

    useEffect(() => {
        if (isOpen) dispatch(fetchSubscriptionProducts());
    }, [dispatch, isOpen]);

    if (!isOpen) return null;

    const handleCheckout = async (product) => {
        const priceId     = product.id;
        const billingCycle = cycleFor(product) || 'monthly';
        setClickedPriceId(priceId);
        setCheckoutError(null);
        try {
            performanceMonitor.start('checkoutSubscription', 'api');
            const response = await dispatch(checkoutSubsciptions({ priceId, billingCycle })).unwrap();
            performanceMonitor.end('checkoutSubscription', 'api');
            if (response?.url) { window.location.href = response.url; return; }
            setCheckoutError('Could not start checkout. Please try again.');
        } catch {
            performanceMonitor.end('checkoutSubscription', 'api', false);
            setCheckoutError('The subscription service is currently unavailable. Please try again later.');
            setTimeout(() => setClickedPriceId(null), 3000);
        }
    };

    // Build all 4 columns: free + sorted paid
    const allPlans = [
        {
            key: 'free',
            title: 'Free',
            price: '$0',
            per: '7-day trial',
            crossed: null,
            isCurrent: isFreeUser,
            isLoading: false,
            meta: PLAN_META.free,
            onSelect: () => isFreeUser && onClose(),
            ctaLabel: isFreeUser ? 'Current plan' : 'Continue free',
            disabled: isFreeUser,
        },
        ...sortedProducts.map((p) => {
            const cycle    = cycleFor(p);
            const isCurrent = isCurrentPaidCard(p);
            return {
                key:       cycle || p.id,
                title:     planTitle(p),
                price:     `$${formatUsd(p.unit_amount)}`,
                per:       intervalLabel(p),
                crossed:   cycle === 'quarterly' ? `$${Math.round(Number(formatUsd(p.unit_amount)) / 3 * 12 / 3 * 4)}` : null,
                isCurrent,
                isLoading: checkoutLoading && clickedPriceId === p.id,
                meta:      PLAN_META[cycle] || PLAN_META.monthly,
                onSelect:  () => !isCurrent && handleCheckout(p),
                ctaLabel:  isCurrent ? 'Current plan' : (PLAN_META[cycle]?.recommended ? 'Continue with Monthly' : cycle === 'quarterly' ? 'Lock in 3 months' : cycle === 'weekly' ? 'Try this week' : 'Get plan'),
                disabled:  checkoutLoading || isCurrent,
                error:     checkoutError && clickedPriceId === p.id ? checkoutError : null,
            };
        }),
    ];

    const modalContent = (
        <div
            className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-6 px-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="relative w-full max-w-5xl">

                {/* close */}
                {!subLoading && !subError && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute -top-1 -right-1 z-10 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-lg text-gray-500 hover:text-gray-900 transition-colors"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                )}

                {/* ── inner white sheet ── */}
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

                    {/* header */}
                    <div className="px-7 pt-7 pb-5 border-b border-[#EFF1F5]">
                        <h2 className="text-2xl font-bold tracking-tight text-[#0F172A] font-dmsans">Choose your plan</h2>
                        <p className="text-[13px] text-[#94A3B8] mt-1 font-dmsans">Monthly is our standard option. Switch billing anytime before checkout.</p>
                    </div>

                    <div className="px-7 py-6 flex flex-col gap-6">

                        {/* ── current plan status strip ── */}
                        {!isFreeUser && (
                            <div
                                className="flex items-center gap-3 px-4 py-3 rounded-xl border"
                                style={{ background: 'linear-gradient(135deg, #EEF1FE 0%, #fff 70%)', borderColor: '#C7C9F1' }}
                            >
                                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#fff', border: '1px solid #C7C9F1' }}>
                                    <Sparkles size={15} style={{ color: '#3F57E0' }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13.5px] font-bold text-[#0F172A] font-dmsans">
                                        You're on <span style={{ color: '#3F57E0' }}>{currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} · Pro</span>
                                    </div>
                                    <div className="text-[11.5px] text-[#64748B] mt-0.5 font-dmsans">
                                        {user?.creditsBalance != null ? `${user.creditsBalance} credits remaining` : 'Active subscription'}
                                        {currentCycleName === 'monthly' ? ' · save 22% by switching to Quarterly' : ''}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="text-[12px] font-semibold font-dmsans text-red-500 hover:text-red-600 transition-colors px-2 py-1"
                                >
                                    Cancel plan
                                </button>
                                {currentCycleName === 'monthly' && (
                                    <button
                                        type="button"
                                        className="text-[12px] font-semibold font-dmsans text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
                                        style={{ backgroundColor: '#3F57E0' }}
                                    >
                                        Switch to Quarterly →
                                    </button>
                                )}
                            </div>
                        )}

                        {/* ── loading skeleton ── */}
                        {subLoading && (
                            <div className="grid grid-cols-4 gap-3">
                                {[1,2,3,4].map(k => (
                                    <div key={k} className="rounded-xl border border-[#E6E8EE] p-5 flex flex-col gap-3">
                                        <div className="h-4 w-16 bg-gray-100 animate-pulse rounded" />
                                        <div className="h-8 w-20 bg-gray-100 animate-pulse rounded" />
                                        <div className="h-3 w-full bg-gray-100 animate-pulse rounded" />
                                        <div className="h-3 w-4/5 bg-gray-100 animate-pulse rounded" />
                                        <div className="h-9 w-full bg-gray-100 animate-pulse rounded-lg mt-2" />
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ── error ── */}
                        {subError && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
                                <p className="text-red-600 font-medium font-dmsans mb-3">Error loading plans</p>
                                <p className="text-[13px] text-red-500 mb-4">{subError}</p>
                                <button
                                    type="button"
                                    onClick={() => dispatch(fetchSubscriptionProducts())}
                                    className="text-[13px] font-semibold text-white px-4 py-2 rounded-lg"
                                    style={{ backgroundColor: '#3F57E0' }}
                                >
                                    Try Again
                                </button>
                            </div>
                        )}

                        {/* ── plan cards ── */}
                        {!subLoading && !subError && (
                            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${allPlans.length}, 1fr)` }}>
                                {allPlans.map((plan) => {
                                    const rec = plan.meta?.recommended;
                                    return (
                                        <div
                                            key={plan.key}
                                            className="relative flex flex-col gap-3 rounded-2xl p-5 transition-all"
                                            style={{
                                                border: rec ? '2px solid #3F57E0' : '1px solid #E6E8EE',
                                                background: rec ? 'linear-gradient(180deg, #FAFBFF 0%, #fff 100%)' : '#fff',
                                                boxShadow: rec ? '0 18px 32px rgba(63,87,224,.12)' : '0 1px 3px rgba(15,23,42,.04)',
                                                transform: rec ? 'translateY(-4px)' : 'none',
                                            }}
                                        >
                                            {/* tag pill */}
                                            {plan.meta?.tag && (
                                                <span
                                                    className="absolute -top-3 left-4 text-[10px] font-bold uppercase tracking-[.08em] text-white px-2.5 py-1 rounded-full"
                                                    style={{ background: rec ? '#3F57E0' : plan.key === 'quarterly' ? '#10B981' : '#0F172A' }}
                                                >
                                                    {plan.meta.tag}
                                                </span>
                                            )}
                                            {plan.isCurrent && (
                                                <span className="absolute -top-3 left-4 text-[10px] font-bold uppercase tracking-[.08em] text-[#3F57E0] bg-[#EEF1FE] px-2.5 py-1 rounded-full">
                                                    Current
                                                </span>
                                            )}

                                            {/* price block */}
                                            <div>
                                                <div className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#94A3B8] font-dmsans">{plan.title}</div>
                                                <div className="flex items-baseline gap-2 mt-1.5 flex-wrap">
                                                    <span className="text-[30px] font-extrabold tracking-tight text-[#0F172A] font-dmsans leading-none">{plan.price}</span>
                                                    {plan.crossed && (
                                                        <span className="text-[12px] text-[#94A3B8] line-through font-dmsans">{plan.crossed}</span>
                                                    )}
                                                </div>
                                                <div className="text-[11.5px] text-[#94A3B8] mt-1 font-dmsans">{plan.per}</div>
                                            </div>

                                            {/* divider */}
                                            <div className="h-px bg-[#EFF1F5]" />

                                            {/* features */}
                                            <ul className="flex flex-col gap-2 flex-1">
                                                {plan.meta?.features.map((f, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-[12.5px] text-[#334155] font-dmsans">
                                                        <span className="w-3.5 h-3.5 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5" style={{ background: '#ECFDF3' }}>
                                                            <Check size={8} style={{ color: '#067647', strokeWidth: 3 }} />
                                                        </span>
                                                        {f}
                                                    </li>
                                                ))}
                                            </ul>

                                            {/* cta */}
                                            <button
                                                type="button"
                                                onClick={plan.onSelect}
                                                disabled={plan.disabled}
                                                className="w-full py-2.5 rounded-xl text-[13px] font-semibold font-dmsans transition-all disabled:opacity-60 mt-auto"
                                                style={{
                                                    background: rec ? '#3F57E0' : plan.isCurrent ? '#EEF1FE' : '#fff',
                                                    color: rec ? '#fff' : plan.isCurrent ? '#3F57E0' : '#0F172A',
                                                    border: rec || plan.isCurrent ? 'none' : '1px solid #E6E8EE',
                                                }}
                                            >
                                                {plan.isLoading ? 'Processing…' : plan.ctaLabel}
                                            </button>
                                            {plan.error && (
                                                <p className="text-[11px] text-red-500 text-center mt-1 font-dmsans">{plan.error}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* ── comparison matrix ── */}
                        {!subLoading && !subError && (
                            <div className="rounded-xl border border-[#E6E8EE] overflow-hidden">
                                {/* matrix header */}
                                <div className="grid border-b border-[#EFF1F5] bg-[#FAFBFD] px-4 py-2.5"
                                    style={{ gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr' }}>
                                    <div className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#94A3B8] font-dmsans">Feature</div>
                                    {['Free', 'Weekly', 'Monthly ★', 'Quarterly'].map((h, i) => (
                                        <div key={h} className="text-center text-[10.5px] font-bold uppercase tracking-[.08em] font-dmsans"
                                            style={{ color: i === 2 ? '#3F57E0' : '#94A3B8' }}>
                                            {h}
                                        </div>
                                    ))}
                                </div>
                                {/* matrix rows */}
                                {COMPARE_ROWS.map((row, i) => (
                                    <div
                                        key={i}
                                        className="grid px-4 py-3 items-center"
                                        style={{
                                            gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr',
                                            borderBottom: i < COMPARE_ROWS.length - 1 ? '1px solid #EFF1F5' : 'none',
                                        }}
                                    >
                                        <div className="text-[13px] font-semibold text-[#0F172A] font-dmsans">{row.label}</div>
                                        <div className="text-center text-[13px] text-[#64748B] font-dmsans">{row.free}</div>
                                        <div className="text-center text-[13px] text-[#334155] font-dmsans">{row.weekly}</div>
                                        <div className="text-center text-[13px] font-bold font-dmsans py-1.5 rounded-md mx-1"
                                            style={{ color: '#3F57E0', background: '#F4F6FF' }}>
                                            {row.monthly}
                                        </div>
                                        <div className="text-center text-[13px] text-[#334155] font-dmsans">{row.quarterly}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ── FAQ row ── */}
                        {!subLoading && !subError && (
                            <div className="grid grid-cols-3 gap-3">
                                {FAQ.map((f, i) => (
                                    <div key={i} className="rounded-xl border border-[#E6E8EE] bg-white p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <HelpCircle size={13} style={{ color: '#3F57E0', flexShrink: 0 }} />
                                            <div className="text-[13px] font-bold text-[#0F172A] font-dmsans">{f.q}</div>
                                        </div>
                                        <p className="text-[12px] text-[#64748B] font-dmsans leading-relaxed">{f.a}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );

    return typeof document === 'undefined' ? modalContent : createPortal(modalContent, document.body);
};

export default SubscriptionModal;
